import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { APP_CONFIG } from "../lib/angular/injection-tokens";
import { IAppConfig } from "../lib/config/app-config";
import { Group, AddGroupDto, GroupList, UpdateGroupListDTO, UpdateGroupDto, GroupEditResult, OrgCentreIdsDto } from "../_models/Group";
import { Observable } from "rxjs";
import { HttpParamsConstants } from "../constants/HttpParamsConstants";
import { Collections } from "../_models/Collection";
import { AppConstants } from "../constants/constants";

@Injectable({
  providedIn: "root",
})
export class GroupService {
  private baseUrl: string = this.config.protectedResourceEndpoint;
  private static readonly path: string = "Groups/";
  private static readonly DISPLAYGROUPLIST_URI: string = "displaygrouplist";
  private static readonly UPDATEGROUPLIST_URI: string = "updategrouplist";
  private static readonly RESETGROUPLIST_URI: string = "resetgrouplist";
  private static readonly GROUPLISTLENGTH_URI: string = "grouplistlength";
  private static readonly GROUPLISTBYORGANDCENTRE_URI: string = "getgrouplistbyorgandcentre";
  private static readonly DELETEGROUPS_URI: string = "deletegroups";
  private static readonly VALIDATEDELETEGROUPS_URI: string = "validatedeletegroups";
  private static readonly GET_PAGE_NUMBER = "getcurrentpagenumber";
  private static readonly GET_LEARNER_IDS_TO_ASSIGN: string = "getlearneridstoassigncourse";
  private static readonly GET_GROUPS_BY_ORG_IDS_CENTRE_OPTIONAL: string = "GetGroupsByOrgIdsOrWithCentreIds";
  private static readonly TUTORGETGROUPSORGIDSCENTRE_URI: string = "tutorflow/GetGroupsByOrgIdsOrWithCentreIds";

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) private config: IAppConfig
  ) {}
  
  public getGroup$(id: number): Observable<Group> {
    return this.http.get<Group>(
      `${this.baseUrl}${GroupService.path}${id}`
    );
  }

  public addGroup$(addGroupDto: AddGroupDto): Observable<Group> {
    return this.http.post<Group>(
      `${this.baseUrl}${GroupService.path}`,
      addGroupDto
    );
  }

  public editGroup$(id: number, dto: UpdateGroupDto): Observable<GroupEditResult> {
    return this.http.put<GroupEditResult>(
      `${this.baseUrl}${GroupService.path}${id}`,
      dto
    );
  }

  public updateGroupTableRecords$(
    updateDto: UpdateGroupListDTO
  ): Observable<boolean> {
    return this.http.put<boolean>(
      this.baseUrl + GroupService.path + GroupService.UPDATEGROUPLIST_URI,
      updateDto
    );
  }

  public getGroupTableRecord$(
    pageNumber: number,
    isInitialLoad: boolean
  ): Observable<GroupList[]> {
    let params = new HttpParams().set(
      HttpParamsConstants.PAGE_NUMBER,
      String(+pageNumber + 1)
    );

    if (isInitialLoad)
      params = params.set(
        HttpParamsConstants.INITIAL_LOAD,
        String(isInitialLoad)
      );

    return this.http.get<GroupList[]>(
      this.baseUrl + GroupService.path + GroupService.DISPLAYGROUPLIST_URI,
      { params }
    );
  }

  public resetGroupTable$(): Observable<boolean> {
    return this.http.get<boolean>(
      this.baseUrl + GroupService.path + GroupService.RESETGROUPLIST_URI
    );
  }

  public getGroupTableLength$(): Observable<number> {
    return this.http.get<number>(
      this.baseUrl + GroupService.path + GroupService.GROUPLISTLENGTH_URI
    );
  }

  public getGroupListByOrgAndCentre$(collections: Collections[]): Observable<Group[]> {
    return this.http.post<Group[]>(
      `${this.baseUrl}${GroupService.path}${GroupService.GROUPLISTBYORGANDCENTRE_URI}`,
      collections
    )
  }

  public deleteGroups$(groupIds: number[]): Observable<Object> {
    return this.http.request(
      AppConstants.DELETE,
      `${this.baseUrl}${GroupService.path}${GroupService.DELETEGROUPS_URI}`,
      { body: groupIds }
    );
  }

  public validateDeleteGroups$(groupIds: number[]): Observable<Object> {
    return this.http.request(
      AppConstants.POST,
      `${this.baseUrl}${GroupService.path}${GroupService.VALIDATEDELETEGROUPS_URI}`,
      { body: groupIds }
    );
  }

  public getCurrentPageNumber$(pageIndex: number): Observable<number> {
    let params = new HttpParams().set(AppConstants.PAGE_NUMBER, String(+pageIndex + 1));
    return this.http.get<number>(
      `${this.baseUrl}${GroupService.path}${GroupService.GET_PAGE_NUMBER}`,
      { params }
    );
  }

  public getLearnerIdsToAssignCourse$(groupIds: number[]): Observable<number[]> {
    return this.http.post<number[]>(
      `${this.baseUrl}${GroupService.path}${GroupService.GET_LEARNER_IDS_TO_ASSIGN}`,
      groupIds
    );
  }

  public getGroupListByOrgAndOptionalCentreIds$(orgCentreIds: OrgCentreIdsDto): Observable<Group[]> {
    return this.http.post<Group[]>(
      `${this.baseUrl}${GroupService.path}${GroupService.GET_GROUPS_BY_ORG_IDS_CENTRE_OPTIONAL}`,
      orgCentreIds
    )
  }

  public getGroupListByOrgAndOptionalCentreIdsTutorFlow$(orgCentreIds: OrgCentreIdsDto): Observable<Group[]> {
    return this.http.post<Group[]>(
      `${this.baseUrl}${GroupService.path}${GroupService.TUTORGETGROUPSORGIDSCENTRE_URI}`,
      orgCentreIds
    )
  }
}
